import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

export type ToneType =
  | 'Calm'
  | 'Assertive'
  | 'Flirty'
  | 'Boundary'
  | 'Professional'

export interface RewriteResult {
  id?: string
  original_text: string
  rewritten_text: string
  tone: ToneType
  toxicity_score?: number
  created_at?: string
}

export interface RewriteError {
  message: string
  isLimitError?: boolean
}

export function useMessageRewriter() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<RewriteError | null>(null)
  const [history, setHistory] = useState<RewriteResult[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const activeSubscriptionRef = useRef<any>(null)

  // Cleanup any active subscription on unmount
  useEffect(() => {
    return () => {
      if (activeSubscriptionRef.current) {
        activeSubscriptionRef.current.unsubscribe()
        activeSubscriptionRef.current = null
      }
    }
  }, [])

  const fetchHistory = useCallback(async () => {
    if (!user?.id) return

    try {
      setLoadingHistory(true)
      const { data, error: fetchError } = await supabase
        .from('rewrites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10)

      if (fetchError) {
        console.error('Error fetching rewrite history:', fetchError)
        return
      }

      setHistory(data || [])
    } catch (err) {
      console.error('Failed to fetch history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const rewriteMessage = async (
    text: string,
    tone: ToneType,
  ): Promise<RewriteResult | null> => {
    if (!user?.id) {
      setError({ message: 'You must be logged in to use this feature.' })
      return null
    }

    // Clean up any previous subscription
    if (activeSubscriptionRef.current) {
      activeSubscriptionRef.current.unsubscribe()
      activeSubscriptionRef.current = null
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Insert a row into the 'rewrites' table
      const { data, error: insertError } = await supabase
        .from('rewrites')
        .insert([
          {
            original_text: text,
            user_id: user.id,
            tone,
          },
        ])
        .select()
        .single()

      // Handle insert errors (RLS policy / credit limit)
      if (insertError) {
        console.error('Insert error:', insertError)

        // RLS violations, permission errors, or credit limit triggers
        const errorMsg = (insertError.message || '').toLowerCase()
        const errorCode = insertError.code || ''

        const isLimitOrRLS =
          errorCode === '42501' || // RLS violation
          errorMsg.includes('policy') ||
          errorMsg.includes('permission') ||
          errorMsg.includes('limit') ||
          errorMsg.includes('credit') ||
          errorMsg.includes('upgrade') ||
          errorMsg.includes('denied') ||
          errorMsg.includes('insufficient')

        if (isLimitOrRLS) {
          const limitError: RewriteError = {
            message:
              "You've used your 3 free daily rewrites. Go Pro for unlimited communication coaching.",
            isLimitError: true,
          }
          setError(limitError)
          setLoading(false)
          return null
        }

        throw new Error(
          insertError.message || 'Failed to submit message for rewriting.',
        )
      }

      if (!data) {
        throw new Error('No data returned from insert.')
      }

      const recordId = data.id

      // Quick check to see if the webhook already populated it
      const { data: checkData } = await supabase
        .from('rewrites')
        .select('rewritten_text, toxicity_score, created_at')
        .eq('id', recordId)
        .single()

      if (checkData && checkData.rewritten_text) {
        const result = {
          id: recordId,
          original_text: text,
          rewritten_text: checkData.rewritten_text,
          tone,
          toxicity_score: checkData.toxicity_score || 0,
          created_at: checkData.created_at || new Date().toISOString(),
        }
        setHistory((prev) => [result, ...prev].slice(0, 10))
        return result
      }

      // 2. Set up Realtime subscription to listen for the AI webhook to update this record
      const result = await new Promise<RewriteResult>((resolve, reject) => {
        const TIMEOUT_MS = 30000 // 30 second timeout

        const timeoutId = setTimeout(() => {
          if (activeSubscriptionRef.current) {
            activeSubscriptionRef.current.unsubscribe()
            activeSubscriptionRef.current = null
          }
          reject(new Error('REWRITE_FAILED'))
        }, TIMEOUT_MS)

        const subscription = supabase
          .channel(`rewrite_status_${recordId}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'rewrites',
              filter: `id=eq.${recordId}`,
            },
            (payload: any) => {
              if (payload.new.rewritten_text) {
                // 3. AI is done — resolve with the result
                clearTimeout(timeoutId)
                subscription.unsubscribe()
                activeSubscriptionRef.current = null

                resolve({
                  id: payload.new.id,
                  original_text: text,
                  rewritten_text: payload.new.rewritten_text,
                  tone,
                  toxicity_score: payload.new.toxicity_score || 0,
                  created_at:
                    payload.new.created_at || new Date().toISOString(),
                })
              }
            },
          )
          .subscribe()

        activeSubscriptionRef.current = subscription
      })

      // Add to history optimistically
      setHistory((prev) => [result, ...prev].slice(0, 10))

      return result
    } catch (err: any) {
      console.error('Rewrite error:', err)
      setError({
        message:
          err.message === 'REWRITE_FAILED'
            ? 'The AI took too long to respond. Please try again.'
            : 'An unexpected error occurred. Please try again.',
      })
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    rewriteMessage,
    history,
    loading,
    loadingHistory,
    error,
    clearError: () => setError(null),
    refreshHistory: fetchHistory,
  }
}
